/**
 * CallButton - Button for initiating business-to-user calls with WebRTC
 * 
 * PHASE 3: Full WebRTC + SDP Support
 * 
 * Features:
 * - Check permission before calling
 * - Request permission if needed
 * - Initiate call with SDP offer
 * - End active calls
 * - Show call status
 */

import React, { useContext, useState } from 'react';
import { CallContext } from '../../../CallContext';
import { GlobalVariablesContext } from '../../../GlobalVariables';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import './CallButton.css';

function CallButton({ disabled = false }) {
    const {
        currentCall,
        agentStatus,
        initiateCall,
        endCall,
        showPermissionDialog,
        clearError,
        connectionState,
    } = useContext(CallContext);

    const { currentUser } = useContext(GlobalVariablesContext);

    const [isLoading, setIsLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');

    // Get phone number from current user
    const phoneNumber = currentUser?.phone || currentUser?.phoneNumber;
    const businessNumber = '918278244128'; // Default business number

    /**
     * Handle button click - initiate call or end active call
     */
    const handleClick = async () => {
        if (!phoneNumber || isLoading) return;

        clearError();
        //setStatusMessage('');

        // If there's an active call, end it
        if (currentCall && currentCall.is_active) {
            setIsLoading(true);
            try {
                const result = await endCall(currentCall.call_id);
                if (!result.success) {
                    //setStatusMessage(result.error || 'Failed to end call');
                }
            } catch (e) {
                //setStatusMessage(e.message);
            } finally {
                setIsLoading(false);
            }
            return;
        }

        // Initiate new call
        setIsLoading(true);
        //setStatusMessage('Requesting mic access...');

        try {
            // initiateCall now handles:
            // 1. Get microphone access
            // 2. Create WebRTC offer
            // 3. Check permission
            // 4. Send to WhatsApp with SDP
            const result = await initiateCall(phoneNumber, businessNumber);

            if (result.success) {
                //setStatusMessage('Calling...');
            } else if (result.error_code === 'PERMISSION_REQUIRED') {
                // Show global permission dialog
                showPermissionDialog(phoneNumber, businessNumber);
                //setStatusMessage('');
            } else {
                //setStatusMessage(result.error || 'Failed to initiate call');
            }
        } catch (e) {
            //setStatusMessage(e.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Determine button state
    const isInCall = currentCall && currentCall.is_active;
    const isBusy = agentStatus === 'busy';
    const isDisabled = disabled || !phoneNumber || isLoading;

    // Button icon and label
    let Icon = PhoneIcon;
    let label = 'Call';

    if (isLoading) {
        label = 'Processing...';
    } else if (isInCall) {
        Icon = PhoneDisabledIcon;
        if (connectionState === 'connected') {
            label = 'End Call';
        } else if (connectionState === 'connecting') {
            label = 'Connecting...';
        } else {
            label = 'End Call';
        }
    } else if (isBusy) {
        label = 'On Call';
    }

    return (
        <div className="call-button-container">
            <button
                className={`call-button-header ${isInCall ? 'in-call' : ''} ${isBusy && !isInCall ? 'busy' : ''}`}
                onClick={handleClick}
                disabled={isDisabled || (isBusy && !isInCall)}
                title={!phoneNumber ? 'No phone number' : (isInCall ? 'End Call' : 'Start Call')}
            >
                <Icon className={isLoading ? 'spinning' : ''} />
                <span className="call-button-label">{label}</span>
            </button>
            {statusMessage && (
                <div className="call-status-message">{statusMessage}</div>
            )}
        </div>
    );
}

export default CallButton;
